export interface PlanStatistics {
  sub_categories: number;
  sub_categories_available: number;
  categories: number;
  categories_available: number;
  characters: number;
  templates: number;
  templates_available: number;
  shortcuts: number;
  shortcuts_available: number;
}

export interface SubscriptionPlan {
  stripe_id?: string;
  identifier: string;
  title: string;
  features: PlanFeatures;
  ends_at?: string | null;
  plan_status?: string;
  on_trial?: boolean;
  /**
   * True while a plan-change charge is still unresolved (flymsg-be #976).
   * Issue #609 AC-2/AC-3: the checkout page's post-payment poll must not
   * treat the identifier alone as "done" — a plan-change race can flip the
   * identifier before the charge that caused it has actually settled.
   */
  payment_pending?: boolean;
  previous_plan?: string;
  pending_plan?: string;
  /** Referral slots — each entry is a JSON string describing a signed-up person (see parseReferrals). */
  referrals?: string[] | null;
  statistics?: PlanStatistics;
  id: string;
  created_at: string;
  updated_at: string;
  currency: string;
  interval: string;
  unit_amount: number;
  referral_key: string;
  used_trial: boolean;
  already_used_pro_trial: boolean;
  user_details: {
    first_name: string,
    last_name: string,
    email: string,
    avatar: string,
    signup_source: string,
    updated_at: string,
    created_at: string,
    email_verified_at: string,
    stripe_id: string,
    onboarding: string,
    last_login: string,
    referral_key: string,
    hubspot_id: string,
    recently_used_get_updated: boolean,
    activation_date: string,
    company_id: string,
    status: string,
    company_group_id: string,
    instancy_id: string,
    is_beta: boolean,
    onboarding_percentage: number,
    onboarding_step: string,
    user_info_id: string,
    last_update_usage_at: string,
    onboardingv2_completed: boolean,
    onboardingv2_presented: boolean,
    onboardingv2_step: number,
    company_name: string,
    phone_number: string,
    title: string,
    tooltip_learning_resources_showed: boolean,
    heap_analytics_id: string,
    reload_grammar_analytics_to_hp5: boolean,
    id: string,
    is_poc: boolean,
    role: string,
    company: {
      name: string,
      slug: string,
      updated_at: string,
      created_at: string,
      instancy_id: string,
      address_line_1: string,
      address_line_2: string,
      city: string,
      country: string,
      state: string,
      zip: string,
      id: string
    }
  },
  has_fly_learning: boolean;
  user_custom_prompts: number;
  user_persona_available: number;
  can_disable_flygrammar: boolean;
  flycut_deployment: number;
  flygrammar_actions: number;
  prompts_per_day: number;
  regenerate_count: number;
}

export interface PlanFeatures {
  Categories: number;
  Subcategory: number;
  ShortcutVersionRollBack: number;
  "Number of FlyCuts that can be created": number;
  "Number of Characters that can be used per flycut": number;
  "Rich Text Editor": string;
  "Spell Check (Language)": boolean;
  Bold: boolean;
  Italic: boolean;
  Underline: boolean;
  Strikethrough: boolean;
  Hyperlink: boolean;
  "Alignment - Left": boolean;
  "Alignment - Centered": boolean;
  "Alignment - Right": boolean;
  "Font Size": number;
  Fonts: number;
  "Bullet Points": boolean;
  "Numbered List": boolean;
  "Increase Indent": boolean;
  "Decrease Indent": boolean;
  "Font Color": boolean;
  "Background Color": boolean;
  "Clear Formatting": boolean;
  "Blocks (Headings)": boolean;
  Preview: boolean;
  "Source Code": boolean;
  "FlyCut Timeout": boolean;
  "Right Click - Open Dashboard": boolean;
  "FlyCuts Imports (JSON/CSV)": boolean;
  "Search Bar": string;
  FlyPlates: number;
  "Latest FlyCuts": boolean;
  Images: boolean;
  "Image Upload Size": number;
  "Media Storage": number;
  "Domain Blacklists": string;
  "Chat Support": {
    "from": string;
    "to": string;
    "timezone": string;
  };
  "Email Support": {
    "from": string;
    "to": string;
    "timezone": string;
  };
  Giphy: boolean;
  "AI Prompts": number;
  "FlyMSG AI Prompts Per Day": number;
}

export enum SubscriptionPlanType {
  Freemium = "Freemium",
  Starter = "Starter",
  Growth = "Growth",
  SalesPro = "sales-pro",
  YearlyStarter = "starter-yearly",
  YearlyGrowth = "growth-yearly",
  YearlySalesPro = "sales-pro-yearly",
  MonthlySalesPro = "sales-pro-monthly",
  ProPlanTeamsSMB = "Pro Plan Teams - SMB",
  ProPlanTeamsEnt = "Pro Plan Teams - ENT",
  ProTeamsSMB = "Pro Teams - SMB",
  ProTeamsEnt = "Pro Teams - ENT",
}

export enum SubscriptionPlanIdentifier {
  Freemium = "freemium",
  Starter = "starter",
  YearlyStarter = "starter-yearly",
  Growth = "growth",
  YearlyGrowth = "growth-yearly",
  SalesPro = "sales-pro-monthly",
  YearlySalesPro = "sales-pro-yearly",
}

export class PlanStatus {
  public static Active = "active";
  public static Paused = "paused";
  public static Trial = "trialing";
  public static PastDue = "past_due";
  public static Canceled = "canceled";
  public static Incomplete = "incomplete";

  public static getNameByValue(value: string): string {
    for (const prop in PlanStatus) {
      if (PlanStatus.hasOwnProperty(prop) && PlanStatus[prop] === value) {
        return prop;
      }
    }
    return "";
  }
}
